'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TestimonialsCarouselProps {
  currentLang?: 'sl' | 'en';
}

export default function TestimonialsCarousel({ currentLang = 'sl' }: TestimonialsCarouselProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials = [
    {
      id: 'testimonial_1',
      name: 'Ana K.',
      location: 'Ljubljana',
      text: currentLang === 'sl' ?'Masaža z medom je bila neverjetna izkušnja. Počutim se bolj sproščeno in polno energije.' :'The honey massage was an incredible experience. I feel more relaxed and full of energy.',
    },
    {
      id: 'testimonial_2',
      name: 'Marko T.',
      location: 'Kranj',
      text: currentLang === 'sl' ?'Inhalacija z dišečim zrakom iz panja mi je pomagala pri težavah z dihali. Priporočam!' :'Inhalation with aromatic air from the hive helped me with respiratory problems. Highly recommend!',
    },
    {
      id: 'testimonial_3',
      name: 'Nina S.',
      location: 'Bled',
      text: currentLang === 'sl' ?'Apiterapija je postala moj mesečni ritual. Naravno in učinkovito.' :'Apitherapy has become my monthly ritual. Natural and effective.',
    },
  ];

  useEffect(() => {
    if (!isHydrated || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isHydrated, isPaused, testimonials.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  if (!isHydrated) {
    return (
      <div className="bg-card card-rounded p-12 shadow-lg space-y-6">
        <div className="h-24 bg-muted rounded animate-pulse"></div>
        <div className="h-4 w-32 bg-muted rounded animate-pulse"></div>
      </div>
    );
  }

  return (
    <div
      className="relative bg-card card-rounded p-12 shadow-lg"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Testimonial Content */}
      <div className="min-h-[200px] flex flex-col justify-center space-y-8 animate-fade-in" key={currentIndex}>
        <div className="text-4xl text-primary opacity-20">"</div>
        <p className="text-xl font-medium leading-relaxed">
          {testimonials[currentIndex].text}
        </p>
        <div className="space-y-1">
          <p className="font-bold">{testimonials[currentIndex].name}</p>
          <p className="text-sm text-muted-foreground">{testimonials[currentIndex].location}</p>
        </div>
      </div>

      {/* Navigation Arrows */}
      <div className="flex items-center justify-between mt-8">
        <button
          onClick={goToPrevious}
          className="p-3 rounded-full bg-muted hover:bg-muted/80 transition-colors"
          aria-label="Previous testimonial"
        >
          <Icon name="ChevronLeftIcon" size={20} />
        </button>

        {/* Indicator Dots */}
        <div className="flex gap-2">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex ? 'bg-primary w-8' : 'bg-muted'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>

        <button
          onClick={goToNext}
          className="p-3 rounded-full bg-muted hover:bg-muted/80 transition-colors"
          aria-label="Next testimonial"
        >
          <Icon name="ChevronRightIcon" size={20} />
        </button>
      </div>
    </div>
  );
}